import type { FC, ReactNode } from 'react'
import type { OverviewData } from '@/data/metrics'
import type { LineChartPoint } from '@/components/charts/line-chart'

import { useState } from 'react'
import { ChartLine, Table2 } from 'lucide-react'
import { formatRupiah, formatSignedPercent, formatSignedRupiahCompact } from '@/lib/format'
import { SectionCard } from '@/components/dashboard/section-card'
import { SegmentedControl } from '@/components/dashboard/segmented-control'
import { LineChart } from '@/components/charts/line-chart'
import { ChartLegend } from '@/components/charts/chart-tooltip'

interface MrrTrendCardProps {
    data: OverviewData
    className?: string
}

interface TrendTableProps {
    points: LineChartPoint[]
    isMonthly: boolean
}

const TrendTable: FC<TrendTableProps> = ({ points, isMonthly }): ReactNode => {
    return (
        <div className='max-h-[312px] overflow-auto rounded-lg border'>
            <table className='w-full text-sm'>
                <caption className='sr-only'>
                    Data tren MRR per {isMonthly ? 'bulan' : 'hari'}
                </caption>

                <thead className='sticky top-0 bg-muted text-xs text-muted-foreground'>
                    <tr>
                        <th scope='col' className='px-3 py-2 text-left font-medium'>{isMonthly ? 'Bulan' : 'Tanggal'}</th>
                        <th scope='col' className='px-3 py-2 text-right font-medium'>MRR</th>
                        <th scope='col' className='hidden px-3 py-2 text-right font-medium sm:table-cell'>Periode sebelumnya</th>
                        <th scope='col' className='px-3 py-2 text-right font-medium'>Selisih</th>
                    </tr>
                </thead>

                <tbody className='divide-y'>
                    {[...points].reverse().map((point) => {
                        const previous = point.previous ?? point.value
                        const change = previous === 0 ? 0 : ((point.value - previous) / previous) * 100

                        return (
                            <tr key={point.key}>
                                <th scope='row' className='px-3 py-2 text-left font-normal whitespace-nowrap text-foreground'>{point.tooltipLabel}</th>
                                <td className='px-3 py-2 text-right whitespace-nowrap tabular-nums'>{formatRupiah(point.value)}</td>
                                <td className='hidden px-3 py-2 text-right whitespace-nowrap text-muted-foreground tabular-nums sm:table-cell'>{formatRupiah(previous)}</td>
                                <td className='px-3 py-2 text-right whitespace-nowrap tabular-nums'>{formatSignedPercent(change)}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export const MrrTrendCard: FC<MrrTrendCardProps> = ({ data, className }): ReactNode => {
    const [view, setView] = useState<'chart' | 'table'>('chart')
    const isMonthly = data.range === '12m'
    const { mrr } = data.kpis

    return (
        <SectionCard
            className={className}
            title='Tren MRR'
            description={isMonthly
                ? 'Posisi MRR di akhir setiap bulan, dibandingkan dengan 12 bulan sebelumnya.'
                : `MRR harian selama ${data.label} terakhir, dibandingkan dengan periode sebelumnya.`}
            action={(
                <SegmentedControl
                    size='sm'
                    label='Tampilan data tren MRR'
                    value={view}
                    onChange={setView}
                    options={[
                        { value: 'chart', label: <ChartLine aria-hidden='true' />, ariaLabel: 'Tampilkan grafik' },
                        { value: 'table', label: <Table2 aria-hidden='true' />, ariaLabel: 'Tampilkan tabel' }
                    ]}
                />
            )}
        >
            <div className='mb-5 flex flex-wrap items-end justify-between gap-x-6 gap-y-3'>
                <div>
                    <p className='text-xs text-muted-foreground'>
                        MRR hari ini
                    </p>
                    <p className='mt-1 flex flex-wrap items-baseline gap-x-2 font-heading text-2xl font-semibold tracking-tight text-foreground tabular-nums'>
                        {formatRupiah(mrr.value)}
                        <span className='text-sm font-medium text-muted-foreground'>
                            {formatSignedRupiahCompact(mrr.value - mrr.previous)} dalam {data.label}
                        </span>
                    </p>
                </div>

                <ChartLegend
                    items={[
                        { key: 'current', label: 'Periode ini', color: 'var(--chart-1)', variant: 'line' },
                        { key: 'previous', label: 'Periode sebelumnya', color: 'var(--chart-5)', variant: 'dashed' }
                    ]}
                />
            </div>

            {view === 'chart'
                ? (
                    <LineChart
                        points={data.trend}
                        seriesLabel='Periode ini'
                        formatValue={formatRupiah}
                        ariaLabel={`Tren MRR ${data.label} terakhir`}
                        height={296}
                    />
                )
                : <TrendTable points={data.trend} isMonthly={isMonthly} />}
        </SectionCard>
    )
}
